import { isVolunteerDelivery } from "./deliveryType";

export interface NBSQRData {
  recipientAccount: string;
  recipientName: string;
  amount: string;
  payerName?: string;
  paymentCode: string;
  purpose?: string;
}

export interface QRValidationResult {
  isValid: boolean;
  errors: string[];
  canGenerateQR: boolean;
}

// Validate Serbian bank account format (must be exactly 18 digits)
export const isValidSerbianBankAccount = (account: string): boolean => {
  if (!account) return false;
  const digits = account.replace(/\D/g, "");
  return digits.length === 18;
};

// Format amount for NBS IPS (RSD format with decimal comma)
export const formatNBSAmount = (amount: number): string => {
  const formatted = amount.toFixed(2).replace(".", ",");
  return `RSD${formatted}`;
};

// Validate order data for QR generation
export const validateOrderForQR = (order: any): QRValidationResult => {
  const errors: string[] = [];

  const isVolunteerDeliveryOrder =
    order.userDeliveryResponse && isVolunteerDelivery(order);

  if (!isVolunteerDeliveryOrder) {
    errors.push(
      "NBS QR kod je dostupan samo za rezervacije koje preuzima ovlašćeno lice"
    );
    return { isValid: false, errors, canGenerateQR: false };
  }

  const volunteerBankAccount = order.userDeliveryResponse?.bankAccount || "";
  const hasValidBankAccount = isValidSerbianBankAccount(volunteerBankAccount);
  const hasValidAmount = order.totalPrice > 0;
  const hasVolunteerInfo =
    order.userDeliveryResponse?.firstName &&
    order.userDeliveryResponse?.secondName;

  if (!hasVolunteerInfo) {
    errors.push("Nedostaju informacije o ovlašćenom licu");
  }

  if (!hasValidBankAccount) {
    errors.push(
      `Nevalidan format bankovnog računa. Račun "${volunteerBankAccount}" ima ${
        volunteerBankAccount.replace(/\D/g, "").length
      } cifara, ali bankovni računi za Srbiju zahtevaju tačno 18 cifara`
    );
  }

  if (!hasValidAmount) {
    errors.push(`Nevalidan iznos rezervacije: ${order.totalPrice}`);
  }

  const canGenerateQR =
    hasValidBankAccount && hasValidAmount && hasVolunteerInfo;

  return {
    isValid: errors.length === 0,
    errors,
    canGenerateQR,
  };
};

// Build NBS IPS QR string according to official specification
export const buildNBSQRString = (data: NBSQRData): string => {
  const parts: string[] = [];

  // Required fields per official NBS specification
  parts.push(`K:PR`); // Identification code
  parts.push(`V:01`); // Version
  parts.push(`C:1`); // Character set (UTF-8)
  parts.push(`R:${data.recipientAccount}`); // Recipient account (18 digits)
  parts.push(`N:${data.recipientName}`); // Recipient name
  parts.push(`I:${data.amount}`); // Amount with RSD prefix and comma

  // Optional fields
  if (data.payerName) {
    parts.push(`P:${data.payerName}`);
  }

  parts.push(`SF:${data.paymentCode}`); // Payment code (289 for cashless)

  if (data.purpose) {
    parts.push(`S:${data.purpose}`); // Purpose (optional)
  }

  return parts.join("|");
};

// Generate complete QR string from order (main function)
export const generateQRStringFromOrder = (
  order: any
): { qrString: string | null; validation: QRValidationResult } => {
  const validation = validateOrderForQR(order);

  if (!validation.canGenerateQR) {
    return { qrString: null, validation };
  }

  const volunteerBankAccount = order.userDeliveryResponse?.bankAccount || "";
  const customerName =
    order.firstName && order.secondName
      ? `${order.firstName} ${order.secondName}`
      : undefined;

  const qrData: NBSQRData = {
    recipientAccount: volunteerBankAccount.replace(/\D/g, ""),
    recipientName: `${order.userDeliveryResponse?.firstName} ${order.userDeliveryResponse?.secondName}`,
    amount: formatNBSAmount(order.totalPrice),
    payerName: customerName,
    paymentCode: "289", // Cashless payment via mobile banking
    purpose: `Rezervacija #${order.id}`,
  };

  const qrString = buildNBSQRString(qrData);

  return { qrString, validation };
};
